import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appActions } from '../../actions/app.actions.js';
import { userActions } from '../../actions/user.actions.js';
import { useTranslation } from 'react-i18next';

import HtmlTooltip from '../HtmlTooltip';
import tooltipImg from '../../img/tooltip.png';
import { useDropzone } from 'react-dropzone';
import {
  Grid,
  Button,
  OutlinedInput,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  CircularProgress,
  ButtonGroup,
} from '@material-ui/core';
import { AudiotrackSharp as AudioIcon, Delete as DeleteIcon } from '@material-ui/icons';

import { dragAndDropStyle } from './shared.styles';
import { useStyles } from './TrainVoicePage.styles';
import { getFilesDuration, getTrainingDuration } from '../../utils/page.utils';
import {
  fetchSubscriptionInfo,
  checkTrainingCompletion,
  uploadTraining,
  uploadModelMethodOne,
  uploadErrorMessage,
} from '../../services/page.services';
import { checkValidLoginStatus } from '../../utils/user.utils';

import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import FreeTrainingMessageModal from '../layout/FreeTrainingMessageModal.js';

function TrainVoicePage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const isAdmin = user?.subscription?.type === 'ADMIN';
  const isCustom = user?.subscription?.type === 'CUSTOM';

  const [uploadType, setUploadType] = useState('train');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [externalFile, setExternalFile] = useState({});
  const [label, setLabel] = useState();
  const [duration, setDuration] = useState(0);
  const [trainingRemainingCount, setTrainingRemainingcount] = useState(0);
  const [trainingInProgress, setTrainingInProgress] = useState(false);
  const [openFreeTrainingModal, setOpenFreeTrainingModal] = useState(false);

  const [needMdx, setNeedMdx] = useState(true);

  useEffect(() => {
    if (userId) (async () => initPage())();
  }, [userId]);

  const trainingDuration = getTrainingDuration(user.subscription?.type, user.subscription?.status === 'past_due');

  const checkTrainingInProgress = async () => {
    const trainings = await checkTrainingCompletion();
    const inProgress = _.some(trainings, { status: 'PENDING' });
    setTrainingInProgress(inProgress);
  };

  const checkRemainingCount = async () => {
    let subscriptionInfo = await fetchSubscriptionInfo();
    dispatch(userActions.updateSubscription(subscriptionInfo));
    setTrainingRemainingcount(subscriptionInfo?.trainingRemaining || 0);
  };

  const initPage = async (isOnSubmit = false) => {
    try {
      if (!isAdmin && !isCustom) {
        await checkTrainingInProgress();
      }
      await checkRemainingCount();
    } catch (e) {
      const message = isOnSubmit ? t('modal.pageLoadFailOnSubmit') : t('modal.pageLoadFail');
      dispatch(appActions.openMessageModal(message, true));
    }
    setExternalFile({});
    setFiles([]);
    setLabel();
    setDuration(0);
  };

  const onDropAccepted = async acceptedFiles => {
    if (checkValidLoginStatus(userId, dispatch)) {
      let max = await getFilesDuration(files);
      const newFileDuration = await getFilesDuration(acceptedFiles);
      const newMaxDuration = max + newFileDuration;
      if (newMaxDuration > trainingDuration.max * 60) {
        dispatch(
          appActions.openMessageModal(
            t('trainingTab.modal.exceedMaxDuration', {
              maxDuration: trainingDuration.max,
              minDuration: trainingDuration.min,
            })
          )
        );
      } else {
        setDuration(max + newFileDuration);
        setFiles(prevState => [...prevState, ...acceptedFiles]);
      }
    }
  };

  const onPathDropAccepted = async acceptedFiles => {
    if (checkValidLoginStatus(userId, dispatch)) {
      setExternalFile({ ...externalFile, path: acceptedFiles[0] });
    }
  };

  const onIndexDropAccepted = async acceptedFiles => {
    if (checkValidLoginStatus(userId, dispatch)) {
      setExternalFile({ ...externalFile, index: acceptedFiles[0] });
    }
  };

  const onDropRejected = async input => {
    if (checkValidLoginStatus(userId, dispatch)) {
      const error = input[0].errors[0];
      if (error.code === 'file-invalid-type') {
        dispatch(appActions.openMessageModal(t('trainingTab.modal.notSupportedFileType')));
      }
    }
  };

  const trainDropzone = useDropzone({
    accept: {
      'audio/mp3': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/mpeg-4': ['.m4a'],
      'audio/flac': ['.flac'],
      'audio/ogg': ['.ogg'],
      // "video/mpeg-4":[".mp4"],
      // "video/x-matroska ":[".mkv"],
    },
    onDropAccepted,
    onDropRejected,
    disabled: loading,
  });

  const pathDropzone = useDropzone({
    accept: {
      '*/*': ['.pth'],
    },
    onDropAccepted: onPathDropAccepted,
    onDropRejected,
    disabled: loading,
  });

  const indexDropzone = useDropzone({
    accept: {
      '*/*': ['.index'],
    },
    onDropAccepted: onIndexDropAccepted,
    onDropRejected,
    disabled: loading,
  });

  const handleExternalUpload = async () => {
    try {
      if (checkValidLoginStatus(userId, dispatch)) {
        if (!label) {
          dispatch(appActions.openMessageModal(t('trainingTab.modal.noLabel')));
          return;
        } else if (!externalFile.path || !externalFile.index) {
          dispatch(appActions.openMessageModal(t('trainingTab.modal.missingFile')));
          return;
        }
        setLoading(true);
        const files = [externalFile.path, externalFile.index];
        await uploadModelMethodOne(userId, files, label);
        await initPage();
      }
    } catch (e) {
      if (e.message) {
        const codeMessage = `code: ${e.code}, message: ${e.message}, config: ${JSON.stringify(e.config)}`;
        await uploadErrorMessage(codeMessage);
      } else {
        await uploadErrorMessage('no message - ' + JSON.stringify(e));
      }
      dispatch(appActions.openMessageModal(t('modal.fileUploadFail')));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (userId, files, label, subscriptionType, needMdx) => {
    try {
      setLoading(true);
      const uploadError = await uploadTraining(userId, files, label, subscriptionType, needMdx);
      if (!!uploadError) {
        if (uploadError === 'pending') {
          dispatch(appActions.openMessageModal(t('modal.trainingInProgressError'), true));
        } else if (uploadError === 'noCount') {
          dispatch(appActions.openMessageModal(t('modal.noRemainingError'), true));
        } else {
          dispatch(appActions.openMessageModal(t('modal.genericError'), true));
        }
      } else {
        await initPage();
      }
    } catch (e) {
      if (e.message) {
        const codeMessage = `code: ${e.code}, message: ${e.message}, config: ${JSON.stringify(e.config)}`;
        await uploadErrorMessage(codeMessage);
      } else {
        await uploadErrorMessage('no message - ' + JSON.stringify(e));
      }
      dispatch(appActions.openMessageModal(t('modal.fileUploadFail')));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    if (checkValidLoginStatus(userId, dispatch)) {
      if (user.subscription?.type === 'FREE' && trainingRemainingCount === 0) {
        setOpenFreeTrainingModal(true);
        return;
      }
      if (!label || files.length === 0 || trainingRemainingCount <= 0 || trainingInProgress) {
        return;
      }
      const durationInMin = Math.ceil(duration / 60);
      if (durationInMin < trainingDuration.min) {
        dispatch(
          appActions.openMessageModal(
            t('trainingTab.modal.minDuration', {
              minDuration: trainingDuration.min,
            })
          )
        );
        return;
      }
      await handleUpload(userId, files, label, user.subscription.type, needMdx);
    }
  };

  const handleButtonClick = uploadType => {
    setUploadType(uploadType);
    setNeedMdx(true);
  };

  const classes = useStyles();
  return (
    <div>
      <FreeTrainingMessageModal open={openFreeTrainingModal} setOpenFreeTrainingModal={setOpenFreeTrainingModal} />
      <div className={classes.pageTitle}>{t('trainingTab.title')}</div>
      <div className={classes.separater} />
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.stepHeader}>
            {t('trainingTab.stepOne.title')}
            <HtmlTooltip
              title={
                <div className={classes.tooltipContainer}>
                  <div className={classes.tooltipHeader}>{t('trainingTab.stepOne.tooltip.titleOne')}</div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>1.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsOne.0')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>2.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsOne.1')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>3.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsOne.2')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>4.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsOne.3')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>5.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsOne.4')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>6.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsOne.5')}</div>
                  </div>
                  <div className={classes.tooltipSubeaderCenter}>{t('trainingTab.stepOne.tooltip.titleTwo')}</div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>1.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsTwo.0')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>2.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsTwo.1')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>3.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsTwo.2')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>4.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsTwo.3')}</div>
                  </div>
                  <div className={classes.tooltipTextContainer}>
                    <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>5.</div>
                    <div className={classes.tooltipText}>{t('trainingTab.stepOne.tooltip.contentsTwo.4')}</div>
                  </div>
                </div>
              }
            >
              <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
            </HtmlTooltip>
          </div>
          <ButtonGroup className={classes.inputSourceButtonContainer}>
            <Button
              className={`${classes.inputSourceButton} ${uploadType == 'train' ? classes.selected : ''}`}
              onClick={() => handleButtonClick('train')}
            >
              {t('trainingTab.tabs.0')}
            </Button>
            <Button
              className={`${classes.inputSourceButton} ${uploadType == 'external' ? classes.selected : ''}`}
              onClick={() => handleButtonClick('external')}
            >
              {t('trainingTab.tabs.1')}
            </Button>
          </ButtonGroup>
          <div className={classes.stepSubHeader}>
            {uploadType === 'train' && (
              <div className={classes.stepSubHeader}>{t('trainingTab.stepOne.trainingSubTitles.0')}</div>
            )}
            {uploadType === 'external' && (
              <div className={classes.stepSubHeader}>{t('trainingTab.stepOne.externalSubTitles.0')}</div>
            )}
          </div>
          {uploadType === 'train' ? (
            <div className={classes.dragAndDropContainer}>
              <div {...trainDropzone.getRootProps({ style: dragAndDropStyle })}>
                <input {...trainDropzone.getInputProps()} />
                <div className={classes.dragAndDrop}>
                  <div className={classes.dragAndDropText}>{t('trainingTab.stepOne.dragAndDropText')}</div>
                  <div className={classes.dragAndDropText}>
                    {t('trainingTab.stepOne.dragAndDropDuration', {
                      maxDuration: trainingDuration.max,
                      minDuration: trainingDuration.min,
                    })}
                  </div>
                  <div className={classes.dragAndDropButton}>{t('trainingTab.stepOne.dragAndDropButton')}</div>
                </div>
              </div>

              <div>
                <List className={classes.dragAndDropList}>
                  {files.map((file, index) => (
                    <ListItem key={index} className={classes.listItem}>
                      <ListItemAvatar className={classes.listItemAvatar}>
                        <Avatar className={classes.avatar}>
                          <AudioIcon className={classes.audioIcon} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText className={classes.listItemText} primary={file.name} />
                      <IconButton
                        onClick={async () => {
                          const newFileList = files.filter((_file, i) => i !== index);
                          setFiles(() => newFileList);
                          setDuration(await getFilesDuration(newFileList));
                        }}
                        edge="end"
                        aria-label="delete"
                      >
                        <DeleteIcon style={{ color: '#fff' }} />
                      </IconButton>
                    </ListItem>
                  ))}
                  <ListItem key={files.length} className={classes.durationText} disableGutters>
                    {t('trainingTab.stepOne.durationText')} : ~{Math.ceil(duration / 60)}{' '}
                    {t('trainingTab.stepOne.durationUnit')}
                  </ListItem>
                </List>
              </div>
              <Grid className={classes.mdxContainer} item xs={12} sm={12} md={12}>
                <FormControl className={classes.radioButtonContainer}>
                  <div className={classes.radioButtonLabel}>
                    {t('trainingTab.stepOne.tabs.fileUpload.vocalExtractionText')}
                    <HtmlTooltip
                      title={
                        <div className={classes.tooltipContainer}>
                          <div className={classes.tooltipHeader}>
                            {t('trainingTab.stepOne.tabs.fileUpload.vocalExtractionTooltip.title')}
                          </div>
                          <div className={classes.tooltipTextContainer}>
                            <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>1.</div>
                            <div className={classes.tooltipText}>
                              {t('trainingTab.stepOne.tabs.fileUpload.vocalExtractionTooltip.contents.0')}
                            </div>
                          </div>
                          <div className={classes.tooltipTextContainer}>
                            <div className={`${classes.tooltipText} ${classes.tooltipTextNumber}`}>2.</div>
                            <div className={classes.tooltipText}>
                              {t('trainingTab.stepOne.tabs.fileUpload.vocalExtractionTooltip.contents.1')}
                            </div>
                          </div>
                        </div>
                      }
                    >
                      <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
                    </HtmlTooltip>
                  </div>
                  <RadioGroup row>
                    <FormControlLabel
                      checked={!needMdx}
                      style={{ color: '#fff' }}
                      control={<Radio style={{ color: '#fff' }} onClick={() => setNeedMdx(false)} />}
                      label={t('trainingTab.stepOne.tabs.fileUpload.vocalExtractionYes')}
                    />
                    <FormControlLabel
                      checked={needMdx}
                      style={{ color: '#fff' }}
                      control={<Radio style={{ color: '#fff' }} onClick={() => setNeedMdx(true)} />}
                      label={t('trainingTab.stepOne.tabs.fileUpload.vocalExtractionNo')}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </div>
          ) : (
            <Grid container>
              <Grid style={{ marginBottom: '0.5rem', marginRight: '1rem' }} item xs={12} sm={12} md={5}>
                <div className={classes.dragAndDropHeader}>PTH File:</div>
                {externalFile.path && (
                  <div className={classes.acceptedFileOuterContainer}>
                    <div className={classes.acceptedFileInnerContainer}>
                      <div>{externalFile.path.name}</div>
                      <DeleteIcon
                        onClick={() => setExternalFile({ ...externalFile, path: null })}
                        className={classes.acceptedFileDelete}
                      />
                    </div>
                  </div>
                )}
                {!externalFile.path && (
                  <>
                    <div
                      {...pathDropzone.getRootProps({
                        style: dragAndDropStyle,
                      })}
                    >
                      <input {...pathDropzone.getInputProps()} />
                      <div className={classes.dragAndDrop}>
                        <div className={classes.dragAndDropText}>{t('trainingTab.stepOne.dragAndDropText')}</div>
                        <div className={classes.dragAndDropButton}>{t('trainingTab.stepOne.dragAndDropButton')}</div>
                      </div>
                    </div>
                  </>
                )}
              </Grid>
              <Grid style={{ marginBottom: '0.5rem', marginRight: '1rem' }} item xs={12} sm={12} md={5}>
                <div className={classes.dragAndDropHeader}>INDEX File:</div>
                {externalFile.index && (
                  <div className={classes.acceptedFileOuterContainer}>
                    <div className={classes.acceptedFileInnerContainer}>
                      <div>{externalFile.index.name}</div>
                      <DeleteIcon
                        onClick={() => setExternalFile({ ...externalFile, index: null })}
                        className={classes.acceptedFileDelete}
                      />
                    </div>
                  </div>
                )}
                {!externalFile.index && (
                  <div {...indexDropzone.getRootProps({ style: dragAndDropStyle })}>
                    <input {...indexDropzone.getInputProps()} />
                    <div className={classes.dragAndDrop}>
                      <div className={classes.dragAndDropText}>{t('trainingTab.stepOne.dragAndDropText')}</div>
                      <div className={classes.dragAndDropButton}>{t('trainingTab.stepOne.dragAndDropButton')}</div>
                    </div>
                  </div>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} sm={12} md={12}>
          <div className={`${classes.separater} ${classes.noMargin}`} />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.stepHeader}>{t('trainingTab.stepTwo.title')}</div>
          <div className={classes.stepSubHeader}>{t('trainingTab.stepTwo.subTitles.0')}</div>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <OutlinedInput
            onChange={e => {
              if (checkValidLoginStatus(userId, dispatch)) {
                setLabel(e.target.value);
              }
            }}
            className={classes.labelInput}
            disabled={loading}
          />
        </Grid>
        <Grid className={classes.buttonGrid} item xs={12} sm={12} md={12}>
          {!loading && uploadType == 'external' && (
            <div className={classes.buttonContainer}>
              <div className={classes.button} onClick={() => handleExternalUpload()}>
                {t('trainingTab.submit.upload')}
              </div>
            </div>
          )}
          {!loading && uploadType == 'train' && (
            <div className={classes.buttonContainer}>
              <div className={classes.button} onClick={handleSubmit}>
                {trainingInProgress && userId
                  ? t('trainingTab.submit.inProgress')
                  : userId && user.subscription?.type !== 'FREE' && trainingRemainingCount <= 0
                    ? t('trainingTab.submit.noRemaining')
                    : t('trainingTab.submit.start')}
              </div>
              <HtmlTooltip
                title={
                  <div className={classes.tooltipContainer}>
                    <div className={classes.tooltipTextContainer}>
                      <div className={`${classes.tooltipText} ${classes.tooltipTextBullet}`}>&#x2022;</div>
                      <div className={classes.tooltipText}>{t('trainingTab.submit.tooltip.contents.0')}</div>
                    </div>
                    <div className={classes.tooltipTextContainer}>
                      <div className={`${classes.tooltipText} ${classes.tooltipTextBullet}`}>&#x2022;</div>
                      <div className={classes.tooltipText}>{t('trainingTab.submit.tooltip.contents.1')}</div>
                    </div>
                    <div className={classes.tooltipTextContainer}>
                      <div className={`${classes.tooltipText} ${classes.tooltipTextBullet}`}>&#x2022;</div>
                      <div className={classes.tooltipText}>{t('trainingTab.submit.tooltip.contents.2')}</div>
                    </div>
                  </div>
                }
              >
                <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
              </HtmlTooltip>
              {user && trainingRemainingCount > 0 && (
                <div className={classes.buttonRemainingText}>
                  {t('trainingTab.submit.remainingCountText')} : {trainingRemainingCount}
                </div>
              )}
            </div>
          )}
          {loading && (
            <div className={classes.laodingContainer}>
              <CircularProgress size="1.4rem" />
              <span className={classes.laodingText}>{t('trainingTab.submit.uploading')}</span>
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default TrainVoicePage;
